<template>
  <section class="final-cta" aria-labelledby="final-cta-title">
    <h2 id="final-cta-title" class="final-cta-title u-display">
      {{ $t("home.finalCta.headline") }}
    </h2>
    <p class="final-cta-subtitle">{{ $t("home.finalCta.subtitle") }}</p>
    <div v-if="isNativeApp" class="final-cta-native">
      <button type="button" @click="startAuth">
        {{ $t("home.hero.nativeCta") }}
      </button>
    </div>
    <StoreBadges v-else class="final-cta-badges" />
  </section>
</template>

<script>
import StoreBadges from "@/components/StoreBadges.vue";
import { authUtils } from "@/scripts/authUtils.js";

export default {
  name: "FinalCtaSection",
  components: { StoreBadges },
  data() {
    return {
      // Same detection HeroSection and StoreBadges use: inside the Capacitor
      // shells the badges render nothing (Apple rejects builds that link to
      // Google Play), which would leave the page's closing band with no call
      // to action at all.
      isNativeApp: window.Capacitor?.isNativePlatform?.() === true,
    };
  },
  methods: {
    startAuth: authUtils.startAuth,
  },
};
</script>

<style scoped>
/* Marketing routes are light-mode only (the pre-paint stamp in
   public/index.html gates dark on /dashboard/ and native). The navy here is a
   deliberately dark *section*, not a dark-mode branch: it is the page's last
   beat before the footer and the strongest boundary on the page. */
.final-cta {
  background: linear-gradient(
    140deg,
    var(--al-ink-900) 0%,
    var(--al-ink-800) 55%,
    var(--al-ink-700) 100%
  );
  /* Never 100vw: with a reserved scrollbar that is wider than the content box
     and gives the whole page a horizontal scrollbar (#704). */
  width: 100%;
  padding: 72px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

/* DESIGN.md type scale: H2 is 26/1.16/700 at every breakpoint. */
.final-cta-title {
  font-size: 26px;
  line-height: 1.16;
  /* public/index.html loads Fraunces at discrete 600/700/800. */
  font-weight: 700;
  color: var(--al-white);
  margin: 0 0 10px;
  max-width: 720px;
  overflow-wrap: break-word;
}

.final-cta-subtitle {
  font-size: 16px;
  line-height: 1.55;
  /* Softened white rather than a muted-gray token: --al-text-muted is #5e6f82,
     which lands at roughly 2.5:1 on this navy. This mix measures ~9.9:1. */
  color: color-mix(in srgb, var(--al-bg-light) 75%, transparent);
  margin: 0 0 24px;
  max-width: 560px;
  overflow-wrap: break-word;
}

/* Official badge artwork is used as-is; Apple's black badge ships its own
   white keyline and is approved for dark backgrounds. StoreBadges' own
   internals are off-limits (its native-shell suppression is App Store review
   load-bearing), so the band restyles it from the outside only. */

/* Inert while IOS_LIVE is true, and load-bearing the moment it is flipped back
   (appStoreLinks.js documents that flip as a supported operation): the caption
   defaults to --al-text-muted, which is unreadable on this band. */
.final-cta-badges :deep(.ios-coming-soon) {
  color: color-mix(in srgb, var(--al-white) 85%, transparent);
}

/* The browser's default focus ring is drawn against the page, not this band;
   on navy it all but disappears. */
.final-cta-badges :deep(a:focus-visible) {
  outline: 3px solid var(--al-white);
  outline-offset: 3px;
  border-radius: var(--al-radius-sm);
}

/* DESIGN.md fixes the primary button as solid --al-primary with white text,
   which is 3.33:1 — fine for WCAG large text (>=18.66px bold) and short of AA
   for anything smaller. 19px/700 is the same threshold HeroSection's native
   CTA sits on. */
.final-cta-native button {
  font-size: 19px;
  font-weight: 700;
  font-family: inherit;
  color: var(--al-white);
  background: var(--al-primary);
  border: none;
  border-radius: var(--al-radius-md);
  padding: 14px 32px;
  min-height: 48px;
  cursor: pointer;
  transition: background-color var(--al-dur-short) var(--al-ease-move);
}

.final-cta-native button:hover,
.final-cta-native button:active {
  background: var(--al-primary-hover);
}

.final-cta-native button:focus-visible {
  outline: 3px solid var(--al-white);
  outline-offset: 2px;
}

@media (max-width: 767px) {
  .final-cta {
    padding: 48px 16px;
  }
}
</style>
